import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput } from "./shared/utils";

interface SendResult {
  ok: true;
  message_id?: string;
  to?: string;
  subject?: string;
  thread_id?: string;
}

interface EmailSendCardProps {
  part: ToolUIPart;
}

export const EmailSendCard = memo(function EmailSendCard({ part }: EmailSendCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<SendResult>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const sendResult = result.kind === "ok" ? result.data : null;

  const accent = isError ? "error" : "success";
  const status = isStreaming ? "streaming" : isError ? "error" : "ready";

  return (
    <CardShell
      icon={isError ? "✗" : "✓"}
      title={isError ? "Send Failed" : "Email Sent"}
      accent={accent}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">⋯</span>
          <div>
            <div className="tool-card__status-text">Sending...</div>
          </div>
        </div>
      )}
      {isError && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✗</span>
          <div>
            <div className="tool-card__status-text">Failed to send</div>
            <div className="tool-card__status-detail">{result.error}</div>
          </div>
        </div>
      )}
      {!isStreaming && !isError && sendResult && (
        <div className="tool-card__status-body">
          <span className="tool-card__status-icon" aria-hidden="true">✓</span>
          <div>
            <div className="tool-card__status-text">
              {sendResult.to ? `Sent to ${sendResult.to}` : "Message sent"}
            </div>
            {sendResult.subject && (
              <div className="tool-card__status-detail">{sendResult.subject}</div>
            )}
          </div>
        </div>
      )}
    </CardShell>
  );
});
